"use client";

import { Button } from "@/components/ui/button";
import Link from "next/link";
import {
  Header as AccordionHeader,
  Trigger as AccordionTrigger,
} from "@radix-ui/react-accordion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { memo } from "react";
import Icon, { IconIdType } from "@/components/ui/Icon";

export type PricingCardProps = {
  iconId: IconIdType;
  title: string;
  priceLabel: string;
  description: string;
  buttonPrimaryLabel: string;
  buttonPrimaryHref?: string;
  buttonSecondaryLabel: string;
  buttonSecondaryHref?: string;
  inheritsFrom?: string;
  features: string[];
  isEnterprise?: boolean;
} & React.ComponentProps<"div">;

const PricingCard = ({
  title,
  iconId,
  priceLabel,
  description,
  buttonPrimaryLabel,
  buttonPrimaryHref = "#",
  buttonSecondaryLabel,
  buttonSecondaryHref = "#",
  inheritsFrom,
  features,
  isEnterprise = false,
  className,
}: PricingCardProps) => {
  const isTablet = useMediaQuery("(min-width: 640px)");

  return (
    <div
      className={cn(
        "flex flex-col border bg-white lg:bg-transparent border-[#BCC2C4] rounded-3xl lg:rounded-2xl lg:border-[#C8D4F3] lg:rounded-2xl overflow-hidden",
        className
      )}
    >
      <div className="flex flex-col p-6 flex-auto sm:flex-initial">
        <div className="grid grid-cols-1 gap-2">
          <div className="flex justify-center w-12 mx-auto lg:w-14 lg:mx-auto">
            <Icon
              id={iconId}
              width={58}
              height={58}
              className="size-14.5 shrink-0 text-blue-500"
            />
          </div>
          <div className="flex text-xl font-semibold lg:text-2xl justify-center lg:font-medium text-gray-800">
            <h4>{title}</h4>
          </div>
        </div>
        <div
          className="mt-1.5 lg:mt-2 flex items-center justify-center text-2xl font-semibold lg:font-medium text-[#1F559E] lg:min-h-12 [&>span]:text-[32px] [&>span]:font-semibold [&>span]:lg:text-2xl [&>span]:lg:font-medium"
          dangerouslySetInnerHTML={{
            __html: priceLabel,
          }}
        />

        <div className="mt-2 border-b-1 h-1 border-light-blue lg:border-[#E2EDFC]" />
        <div className="mt-[19] text-base leading-[160%] lg:leading-[150%] flex sm:h-[168px]">
          <p>{description}</p>
        </div>
        <div className="grid grid-cols-1 gap-4 pt-6 mt-auto">
          <div className="flex justify-center">
            <Button
              asChild
              variant="primary"
              className="flex-auto min-w-0 h-[48]"
            >
              <Link href={buttonPrimaryHref}>{buttonPrimaryLabel}</Link>
            </Button>
          </div>
          <div className="flex justify-center">
            <Button
              asChild
              variant="primary-outline"
              className="flex-auto min-w-0 lg:hidden h-[48]"
            >
              <Link 
                href={buttonSecondaryHref}
                aria-label={`${buttonSecondaryLabel} about ${title}`}
                title={`${buttonSecondaryLabel} about ${title}`}
              >
                {buttonSecondaryLabel}
              </Link>
            </Button>
            <Link
              href={buttonSecondaryHref}
              className="text-[#2F80ED] font-semibold text-[14px] flex-auto min-w-0 text-center hidden lg:block focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
              aria-label={`${buttonSecondaryLabel} about ${title}`}
              title={`${buttonSecondaryLabel} about ${title}`}
            >
              {buttonSecondaryLabel}
            </Link>
          </div>
        </div>
      </div>
      <Accordion
        type="multiple"
        className="flex flex-auto bg-background-secondary sm:bg-white sm:pt-6 pb-2"
      >
        <AccordionItem value={title} className="flex-auto min-w-0">
          <AccordionHeader className="flex flex-auto min-w-0 sm:hidden" asChild>
            <div>
              <AccordionTrigger
                data-slot="accordion-trigger"
                className={cn(
                  "group p-6 pb-4 text-blue-500 font-semibold text-base leading-[150%] flex-auto min-w-0 text-center flex items-center justify-center gap-2 rounded-b-3xl",
                  className
                )}
              >
                <span>View more details</span>
                <span
                  className={
                    "w-6 h-6 shrink-0 relative flex items-center justify-center before:absolute before:m-auto before:bg-blue-500 before:w-4 before:h-0.5 before:rounded-[10px] before:will-change-transform before:transition-transform before:[transform-style:preserve-3d] before:[transform:translate3d(0,0,0)translateZ(0)scale3d(1,1,1)rotateX(0deg)rotateY(0deg)rotateZ(0deg)] after:absolute after:m-auto after:bg-blue-500 after:w-4 after:h-0.5 after:rounded-[10px] after:will-change-transform after:transition-transform after:[transform:translate3d(0,0,0)translateZ(0)scale3d(1,1,1)rotateX(0deg)rotateY(0deg)rotateZ(90deg)] after:[transform-style:preserve-3d] group-data-[state=open]:before:[transform:translate3d(0,0,0)translateZ(0)scale3d(1,1,1)rotateX(0deg)rotateY(0deg)rotateZ(180deg)skew(0deg,0deg)] group-data-[state=open]:after:[transform:translate3d(0,0,0)translateZ(0)scale3d(1,1,1)rotateX(0deg)rotateY(0deg)rotateZ(180deg)skew(0deg,0deg)] group-data-[state=open]:before:[transform-style:preserve-3d] group-data-[state=open]:after:[transform-style:preserve-3d]"
                  }
                />
              </AccordionTrigger>
            </div>
          </AccordionHeader>
          <AccordionContent
            {...(isTablet === false ? {} : { forceMount: true })}
            className={cn(
              "p-0 sm:!grid grid-cols-1 place-content-start text-gray-800 px-6 pb-4 text-base leading-[150%] sm:text-[14px]",
              {
                "lg:h-[420]": isEnterprise,
                "lg:h-full": !isEnterprise,
                hidden: isTablet === undefined,
              }
            )}
          >
            {!!inheritsFrom && (
              <div className="flex items-center font-bold sm:font-semibold">
                <h4>{inheritsFrom}</h4>
              </div>
            )}
            <ul className="flex flex-col gap-3 sm:gap-4 not-first:mt-3 sm:not-first:mt-6">
              {features.map((feature, i) => {
                return (
                  <li
                    key={i}
                    className="flex gap-2 leading-[130%] sm:leading-[150%]"
                  >
                    <span className="inline-flex justify-center items-center shrink-0 self-start w-6 h-6 sm:w-auto sm:h-auto">
                      <Icon
                        id="icon-check_20"
                        className="w-[13] text-blue-500 sm:w-[19] sm:h-[29] sm:text-[#34ad68] shrink-0"
                      />
                    </span>
                    <div className="flex items-center">
                      <span>{feature}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default memo(PricingCard);
